import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  applyResolvedTheme,
  DEFAULT_THEME_MODE,
  getSystemPrefersDark,
  readStoredThemeMode,
  resolveTheme,
  ResolvedTheme,
  ThemeMode,
  writeStoredThemeMode,
} from "./themeTokens";

type ThemeContextValue = {
  mode: ThemeMode;
  resolvedTheme: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

const getInitialResolvedTheme = (): ResolvedTheme => {
  if (typeof document !== "undefined") {
    const existingTheme = document.documentElement.getAttribute("data-theme");
    if (existingTheme === "dark" || existingTheme === "light") {
      return existingTheme;
    }
  }
  return "light";
};

export const ThemeProvider = ({
  children,
}: React.PropsWithChildren<Record<string, never>>) => {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredThemeMode());
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    getInitialResolvedTheme()
  );

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      const nextResolvedTheme = resolveTheme(mode, false);
      setResolvedTheme(nextResolvedTheme);
      applyResolvedTheme(nextResolvedTheme);
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const syncTheme = () => {
      const nextResolvedTheme = resolveTheme(mode, mediaQuery.matches);
      setResolvedTheme(nextResolvedTheme);
      applyResolvedTheme(nextResolvedTheme);
    };

    syncTheme();

    const handleSystemThemeChange = () => {
      if (mode === "system") {
        syncTheme();
      }
    };

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleSystemThemeChange);
    } else {
      mediaQuery.addListener(handleSystemThemeChange);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", handleSystemThemeChange);
      } else {
        mediaQuery.removeListener(handleSystemThemeChange);
      }
    };
  }, [mode]);

  useEffect(() => {
    writeStoredThemeMode(mode);
  }, [mode]);

  const setMode = (nextMode: ThemeMode) => {
    setModeState(nextMode);
  };

  const toggleTheme = () => {
    const currentResolvedTheme = resolveTheme(mode, getSystemPrefersDark());
    setModeState(currentResolvedTheme === "dark" ? "light" : "dark");
  };

  const contextValue = useMemo(
    () => ({
      mode,
      resolvedTheme,
      setMode,
      toggleTheme,
    }),
    [mode, resolvedTheme]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const contextValue = useContext(ThemeContext);
  if (!contextValue) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return contextValue;
};
